"use client";

import React, { useState } from "react";
import { useAtom, useSetAtom } from "jotai";
import { MissionType } from "@/types";
import {
  missionSidebarSearchValueAtom,
  missionSidebarSearchVisibilityAtom,
} from "@/store/mission";
import { MissionList } from "@/components";
import { sidebarVisibilityAtom, navigationAtom } from "@/store";
import { Input, Badge, Toggle, Button } from "@oreme/ui";
import { ListFilter, X } from "lucide-react";
import { useMapLibre } from "@/hooks";
import { useTranslations } from "next-intl";

type MissionSidebarProps = {
  data: MissionType[];
};

const filterData = (data: MissionType[], search: string) => {
  return data.filter((mission) => {
    return (
      mission.mission_num2.toLowerCase().includes(search.toLowerCase()) ||
      mission.title.toLowerCase().includes(search.toLowerCase())
    );
  });
};

function MissionSidebar({ data }: Readonly<MissionSidebarProps>) {
  const t = useTranslations("mission.MissionSidebar");
  const { map } = useMapLibre();
  const setNavigation = useSetAtom(navigationAtom);
  const toggleSidebarVisibility = useSetAtom(sidebarVisibilityAtom);
  const [searchValue, setSearchValue] = useAtom(missionSidebarSearchValueAtom);
  const [isSearchVisible, toggleSearchVisibility] = useAtom(
    missionSidebarSearchVisibilityAtom,
  );
  const [filteredData, setFilteredData] = useState(
    filterData(data, searchValue),
  );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    setFilteredData(filterData(data, value));
  };

  const handleClose = () => {
    toggleSidebarVisibility();
    setNavigation(null);
    map!.resize();
  };

  const handleClear = () => {
    setSearchValue("");
    setFilteredData(filterData(data, ""));
  };

  return (
    <div className="flex-1 flex flex-col w-full h-full">
      <header className="flex flex-row items-center justify-start pl-4 pr-2 h-12 rounded-none m-0 gap-3">
        <h3 className="text-base flex flex-row gap-3 items-center">
          <span>{t("title")}</span>
          <Badge variant="outline">{data.length}</Badge>
        </h3>
        <div className="flex justify-center items-center ml-auto gap-1">
          <Toggle
            className="p-0 m-0"
            title={t("filter")}
            aria-label={t("filter")}
            pressed={isSearchVisible}
            size="xs"
            onPressedChange={toggleSearchVisibility}
          >
            <ListFilter size={16} />
          </Toggle>
          <Button
            className="border-0 size-6"
            size="icon-sm"
            onClick={() => {
              handleClose();
            }}
            variant="ghost"
            title={t("close")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </header>
      <form
        className={`relative flex-row justify-between items-center px-4 py-3 border-t ${isSearchVisible ? "flex" : "hidden"}`}
        onSubmit={(e) => e.preventDefault()}
      >
        <Input
          className="text-xs pr-8"
          placeholder={t("search")}
          onChange={handleSearch}
          value={searchValue}
        />
        {searchValue.length > 0 ? (
          <Button
            size="icon"
            onClick={() => {
              handleClear();
            }}
            variant="link"
            title={t("clear")}
            className="absolute right-5"
          >
            <X className="h-3 w-3" />
          </Button>
        ) : null}
      </form>
      <div className="relative flex-1 overflow-hidden border-t">
        <MissionList data={filteredData} />
      </div>
    </div>
  );
}

export { MissionSidebar };
